/**
 * Send feedback: one box, sent to the owner with the platform, the app's version and the page it was sent from. Opens
 * from the account menu (bottom left) and, in the Mac app, Help, Send Feedback. What was typed is kept until the server
 * has it: closing the dialog, a failed send (offline, say) or a reload leaves the draft where it was. The page is the
 * route's path only; a search's query string stays out of it.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FEEDBACK_CONTEXT_MAX, FEEDBACK_MESSAGE_MAX, type FeedbackPlatform } from "@hub/shared";
import { Button, Modal, Textarea, toast } from "@/ui";
import { ApiError, api } from "@/api/client";
import { useLayoutStore } from "@/app/navigation";
import { platformForHost, useSession } from "@/app/session";
import { getHost } from "@/engine/transport";

const DRAFT_KEY = "hub_feedback_draft";

function readDraft(): string {
  try {
    return localStorage.getItem(DRAFT_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeDraft(text: string): void {
  try {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // The draft still lives in the dialog's state.
  }
}

/** The Mac app or a browser (the extension's side panel counts as the web). */
export function feedbackPlatformForHost(): FeedbackPlatform {
  return platformForHost() === "darwin" ? "mac" : "web";
}

/** A browser build's label: the release number with the commit beside it ("0.1.19 (e67ad57)"), either alone when only one is known. */
export function webBuildLabel(): string | null {
  const release = typeof __HUB_RELEASE__ !== "undefined" ? __HUB_RELEASE__ : null;
  const commit = typeof __HUB_BUILD__ !== "undefined" ? __HUB_BUILD__ : null;
  return release && commit ? `${release} (${commit})` : release ?? commit;
}

/** The Mac app's version from the shell, or the browser build's label. */
export async function appVersionForHost(): Promise<string | null> {
  const fromShell = await getHost()?.shell?.getVersion?.().catch(() => null);
  if (fromShell) return fromShell;
  return webBuildLabel();
}

function failureText(e: unknown): string {
  if (e instanceof ApiError && e.isNetwork) return "Could not reach Hub. What you wrote is kept; send it again when you are online.";
  return `${(e instanceof Error ? e.message : "It did not send").replace(/[.\s]+$/, "")}. What you wrote is kept.`;
}

export function SendFeedbackDialog() {
  const open = useLayoutStore((st) => st.feedbackOpen);
  const setOpen = useLayoutStore((st) => st.setFeedbackOpen);
  const signedIn = useSession((st) => st.isSignedIn);
  const location = useLocation();
  const [text, setText] = useState(readDraft);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => writeDraft(text), [text]);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const close = () => setOpen(false);
  const message = text.trim();
  const send = async () => {
    if (!message || sending || !signedIn) return;
    setSending(true);
    setError(null);
    try {
      await api.feedback.send({ message, platform: feedbackPlatformForHost(), appVersion: await appVersionForHost(), context: location.pathname.slice(0, FEEDBACK_CONTEXT_MAX) });
      setText("");
      setOpen(false);
      toast("Thanks. Your feedback was sent.", "success");
    } catch (e) {
      setError(failureText(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Send feedback"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!message || !signedIn} loading={sending} onClick={() => void send()} data-testid="feedback-send">
            Send
          </Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 8 }}>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="What works, what does not, what you wish Hub did"
          aria-label="Feedback"
          rows={6}
          maxLength={FEEDBACK_MESSAGE_MAX}
          autoFocus
        />
        {error ? (
          <div className="u-small" style={{ color: "var(--danger)" }} role="alert">
            {error}
          </div>
        ) : (
          <div className="u-small u-muted">{signedIn ? "Goes to the Hub team with this app's version and the page you are on." : "Sign in to send feedback, or write to hello@thyic.com."}</div>
        )}
      </div>
    </Modal>
  );
}
